import { useQueries, useQuery } from "@tanstack/react-query";

import {
  getRecord,
  getRecordTypes,
  loadRecordNames,
  type RecordEntry,
} from "../records/records-api.js";
import {
  listTaskOccurrences,
  type TaskOccurrence,
} from "../tasks/tasks-api.js";
import type { AttentionLookups } from "./attention-rows.js";
import type { FollowUp } from "./followups-api.js";

async function listMissedOccurrences(
  locationId: string,
): Promise<TaskOccurrence[]> {
  const items: TaskOccurrence[] = [];
  let cursor: string | undefined;
  do {
    const page = await listTaskOccurrences(
      locationId,
      { assignee: "any", status: "missed" },
      cursor,
    );
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return items;
}

// Names, readings and times that turn follow-ups into readable rows.
export function useAttentionLookups(
  locationId: string | undefined,
  items: readonly FollowUp[],
): AttentionLookups {
  const enabled = Boolean(locationId) && items.length > 0;
  const hasMissed = items.some((item) => item.trigger_kind === "missed_task");
  const recordIds = [
    ...new Set(
      items.flatMap((item) => (item.record_id ? [item.record_id] : [])),
    ),
  ];
  const occurrences = useQuery({
    enabled: enabled && hasMissed,
    queryFn: () => listMissedOccurrences(locationId!),
    queryKey: ["attention", "missed-occurrences", locationId],
    staleTime: 60_000,
  });
  const names = useQuery({
    enabled: enabled && recordIds.length > 0,
    queryFn: () => loadRecordNames(locationId!, true),
    queryKey: ["record-names", locationId, true],
    staleTime: 300_000,
  });
  const types = useQuery({
    enabled: enabled && recordIds.length > 0,
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const records = useQueries({
    queries: recordIds.map((id) => ({
      queryFn: () => getRecord(id),
      queryKey: ["record", id],
      staleTime: 60_000,
    })),
  });

  return {
    names: names.data ?? new Map(),
    occurrences: new Map(
      (occurrences.data ?? []).map((entry) => [entry.id, entry]),
    ),
    records: new Map(
      records
        .map((query) => query.data)
        .filter((entry): entry is RecordEntry => Boolean(entry))
        .map((entry) => [entry.id, entry]),
    ),
    typeTitles: new Map(
      (types.data ?? []).map((type) => [type.code, type.title]),
    ),
  };
}
